import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useOrchestratorContext, clearOrchestratorContext } from './useOrchestratorContext';
import { markOrchestratorStepDone } from '../services/smartStartApi';

interface SyncOptions {
  /**
   * If true, fires markStepDone whenever `signal` becomes truthy (idempotent —
   * backend skips if already completed). Use this to silently mirror host-screen
   * state changes (e.g. AI Council session approved → mark step done).
   */
  autoSyncSignal?: any;
  /**
   * Artifact metadata to attach when the step is auto-marked done.
   */
  autoSyncArtifact?: Record<string, any>;
  autoSyncSummary?: string;
}

interface SyncResult {
  inRouter: boolean;
  sessionId: number | null;
  stepKey: string | null;
  subStep: string | null;
  /**
   * Manually mark the current routed step as complete. Used for the banner's
   * "Finish for Smart Router" click handler. Returns void; redirects on success.
   */
  finishStep: (artifact?: Record<string, any>, summary?: string) => Promise<void>;
}

/**
 * Reusable hook for any host screen that wants to play nicely with Smart Router.
 *
 * Pattern: while inRouter is true, the host screen renders <SmartRouterBanner/>;
 * when the work for the current step is done, it sets `autoSyncSignal` to a
 * truthy value (e.g. an artifact id) and the hook marks the step complete in
 * the background. The user stays on the screen.
 *
 * Manual finish (banner button) calls finishStep() which marks done + redirects.
 */
export function useSmartRouterStepSync(options: SyncOptions = {}): SyncResult {
  const ctx = useOrchestratorContext();
  const lastSyncedRef = useRef<string | null>(null);

  const stepKey = ctx.stepKey;
  const sessionId = ctx.sessionId;

  // Background auto-sync when host signals "work for this step is done".
  useEffect(() => {
    if (!ctx.inRouter || sessionId == null || !stepKey) return;
    if (!options.autoSyncSignal) return;
    const fingerprint = `${sessionId}:${stepKey}:${String(options.autoSyncSignal)}`;
    if (lastSyncedRef.current === fingerprint) return;
    lastSyncedRef.current = fingerprint;
    markOrchestratorStepDone(sessionId, stepKey, {
      artifact: options.autoSyncArtifact,
      summary: options.autoSyncSummary,
    }).catch(() => {
      lastSyncedRef.current = null;
    });
  }, [
    ctx.inRouter,
    sessionId,
    stepKey,
    options.autoSyncSignal,
    options.autoSyncArtifact,
    options.autoSyncSummary,
  ]);

  const finishStep = useCallback(
    async (artifact?: Record<string, any>, summary?: string) => {
      if (!ctx.inRouter || sessionId == null || !stepKey) return;
      try {
        await markOrchestratorStepDone(sessionId, stepKey, { artifact, summary });
        toast.success('Step finished — returning to Smart Router.');
        // Always do a full navigation back to the plan view. Same-screen
        // URL swaps left stale component state (old session, old docs)
        // because the route didn't actually change — the user got "stuck"
        // on the previous step's output. Bouncing through the plan resets
        // everything cleanly and lets the user click Open on the next step.
        const target = ctx.returnTo || `/app/smart-start/${sessionId}`;
        clearOrchestratorContext();
        window.location.href = target;
      } catch (err: any) {
        toast.error(`Could not finish: ${err?.response?.data?.error || err?.message || 'unknown error'}`);
      }
    },
    [ctx.inRouter, ctx.returnTo, sessionId, stepKey],
  );

  return {
    inRouter: ctx.inRouter,
    sessionId,
    stepKey,
    subStep: ctx.subStep,
    finishStep,
  };
}
